import React from 'react';
import {useParams} from 'react-router-dom';
import {
  FeatureFlagEnum,
  ModuleEnum,
  PermissionOperationEnum,
} from 'src/api/ProactApiClient';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {OfflineStatusPill} from 'src/app/offline/OfflineStatusPill';
import {SidebarLinkInspections} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {InspectionManageTab} from 'src/pages/Modules/Inspections/InspectionList/components/InspectionManageTab';
import {InspectionMyTab} from 'src/pages/Modules/Inspections/InspectionList/components/InspectionMyTab';
import {InspectionSchedulesTab} from 'src/pages/Modules/Inspections/InspectionList/components/InspectionSchedulesTab';
import {DeviceTable} from 'src/pages/Modules/Inspections/shared/DeviceTable';
import {EquipmentTypeTable} from 'src/pages/Modules/Inspections/shared/EquipmentTypeTable';
import {InspectionTypeTable} from 'src/pages/Modules/Inspections/shared/InspectionTypeTable';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {useConnectivity} from 'src/offline/useConnectivity';
import {useInspectionPermissions} from 'src/shared/auth/permissions/useInspectionPermissions';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import type {InspectionIndexViewType} from 'src/shared/constants/ViewTypes';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {useUrlQueryTabV2} from 'src/shared/hooks/useUrlQueryTabV2';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

// Tabs backed by list queries that never divert to an offline session.
const NETWORK_ONLY_VIEWS: InspectionIndexViewType[] = [
  'manage',
  'schedules',
  'devices',
  'equipment-types',
  'inspection-types',
];

export const InspectionListPage: React.FC = () => {
  const navigate = useAppNavigate();
  useSetDocumentTitle('Inspections');
  const {view} = useParams();

  const perms = useInspectionPermissions();
  const canManage = perms.manage || perms.manageAll;
  const {isOffline} = useConnectivity();

  // useUrlQueryTabV2 only hides the tab button for `hidden` tabs; sanitize an
  // unauthorized 'manage'/'schedules' request to 'list' below.
  const requestedView = (view ?? '') as InspectionIndexViewType;
  const activeView =
    (requestedView === 'manage' && !canManage) ||
    (requestedView === 'schedules' && !perms.manageSchedules) ||
    (isOffline && NETWORK_ONLY_VIEWS.includes(requestedView))
      ? 'list'
      : requestedView;

  const {RenderedTabsComponent} = useUrlQueryTabV2<InspectionIndexViewType>({
    view: activeView,
    defaultView: 'list',
    tabs: [
      {
        view: 'list',
        label: 'My Inspections',
        component: <InspectionMyTab />,
      },
      {
        view: 'manage',
        label: 'Manage Inspections',
        hidden: !canManage,
        disabled: isOffline,
        component: <InspectionManageTab />,
      },
      {
        view: 'schedules',
        label: 'Schedules',
        hidden: !perms.manageSchedules,
        disabled: isOffline,
        component: <InspectionSchedulesTab />,
      },
      {
        view: 'devices',
        label: 'Devices',
        disabled: isOffline,
        component: <DeviceTable identifier="device-list-page" />,
      },
      {
        view: 'equipment-types',
        label: 'Equipment Types',
        disabled: isOffline,
        component: <EquipmentTypeTable />,
      },
      {
        view: 'inspection-types',
        label: 'Inspection Types',
        disabled: isOffline,
        component: <InspectionTypeTable />,
      },
    ],
    onSelectView: view =>
      navigate(PathUtility.Inspections(view as InspectionIndexViewType)),
  });

  return (
    <ContentWrapper featureFlag={FeatureFlagEnum.Inspections}>
      <PageHeader>
        <PageHeader.Title icon={SidebarLinkInspections.icon}>
          Inspections
        </PageHeader.Title>
        <PageHeader.Actions>
          <OfflineStatusPill />
        </PageHeader.Actions>
      </PageHeader>
      {RenderedTabsComponent}
    </ContentWrapper>
  );
};

// Register the route with the new system
routeRegistry.registerPageRoute({
  backNavigation: 'page',
  path: PathUtility.Inspections(':view'),
  element: <InspectionListPage />,
  module: ModuleEnum.Inspections,
  operation: PermissionOperationEnum.View,
  isPrivate: true,
  supportsOffline: true,
});
